import { Icon, Typography } from '@smartgift/frontend-toolkit';
import sortBy from 'lodash/sortBy';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';

import { useValidationErrorsStore } from '@hero/components/providers/ValidationErrorsProvider';
import { configs } from '@hero/configs';
import { VALIDATION_ERRORS } from '@hero/constants';
import { trimStringsInObject } from '@hero/utils';
import { isEmailValid } from '@hero/utils/validationUtils';

import { SpreadSheetRecipientTable } from '../../Contacts';
import { FileUploadArea } from '../../Contacts/NewContactModal/SpreadSheet/FileUploadArea';

import { REVIEW_IMPORT_STEP, STEP } from './constants';
import * as S from './style';

const csvHeader = ['email', 'firstName'];

const INNER_STEP = {
  upload: 'upload',
  table: 'table'
};

function UploadSpreadSheet({
  isLoading,
  setIsLoading,
  setUploadedRecipients,
  setActiveScene,
  uploadedRecipients,
  setFooterProps,
  setStep
}) {
  const { t } = useTranslation();
  const [innerStep, setInnerStep] = useState(INNER_STEP.upload);
  const { addModalError, reset } = useValidationErrorsStore();

  const handleFile = async file => {
    setIsLoading(true);
    await new Promise(resolve => {
      let message = '';
      const reader = new FileReader();
      reader.readAsBinaryString(file);
      reader.onload = async ({ target: { result } }) => {
        const XLSX = await import('xlsx');
        const wb = XLSX.read(result, { type: 'binary', cellDates: true });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];

        const range = XLSX.utils.decode_range(ws['!ref']);
        // TODO: isEmailValid is not correct way, we should check like equal "email" and other columns
        range.s.r = isEmailValid(ws?.A1?.h) ? 0 : 1; // <-- if file has header index will set as 1 and skip row 0
        ws['!ref'] = XLSX.utils.encode_range(range);

        const recipients = XLSX.utils.sheet_to_json(ws, {
          header: csvHeader,
          raw: true,
          rawNumbers: false
        });

        if (recipients.length) {
          message = t('recipientsSuccessfullyAdded', { count: recipients.length });
        } else {
          message = t('noRecipientsAreAdded');
        }

        toast(
          {
            title: message,
            message: t('addMoreRecipientsOrContinue')
          },
          {
            className: 'toast-wrapper success'
          }
        );

        setUploadedRecipients(oldUploadedRecipients => [
          ...oldUploadedRecipients,
          ...sortBy(
            recipients.map((recipient, key) => {
              const trimRecipient = trimStringsInObject(recipient);
              return {
                id: key,
                tags: [],
                hasError: !isEmailValid(trimRecipient.email),
                ...trimRecipient
              };
            }),
            [a => !a.hasError]
          )
        ]);

        resolve();
        setIsLoading(false);
      };
    });
  };

  const checkCustomErrors = async ({ hasErrorRecipients, uploadNextButtonClick }) => {
    let hasCustomError = false;

    if (innerStep === INNER_STEP.table) {
      if (hasErrorRecipients?.length) {
        await addModalError(
          <Trans
            i18nKey={VALIDATION_ERRORS.missingRecipientInformation}
            components={{ strong: <strong /> }}
          />
        );
        hasCustomError = true;
      } else {
        await reset();
      }
    }
    if (innerStep === INNER_STEP.upload && !uploadedRecipients?.length && uploadNextButtonClick) {
      await addModalError(
        <Trans i18nKey={VALIDATION_ERRORS.uploadSpreadsheet} components={{ strong: <strong /> }} />
      );
      hasCustomError = true;
    }
    return hasCustomError;
  };

  const onUploadNextButtonClick = async () => {
    const hasError = await checkCustomErrors({ uploadNextButtonClick: true });

    if (!hasError) {
      setInnerStep(INNER_STEP.table);
    }
  };

  const hasErrorRecipients = useMemo(() => {
    return uploadedRecipients.filter(r => r.hasError);
  }, [uploadedRecipients]);

  useEffect(() => {
    checkCustomErrors({ hasErrorRecipients });
  }, [hasErrorRecipients, innerStep]);

  useEffect(() => {
    if (innerStep === INNER_STEP.upload) {
      setFooterProps({
        leftButtonText: t('cancel'),
        leftButtonOnClick: () => setActiveScene(STEP.select),
        isLeftButtonActive: true,
        isRightButtonActive: !isLoading,
        rightButtonOnClick: onUploadNextButtonClick,
        rightButtonText: t('next'),
        rightButtonEndIcon: <Icon name="next" />,
        activeStepIndex: 1
      });
    } else if (innerStep === INNER_STEP.table) {
      setFooterProps({
        leftButtonText: t('cancel'),
        leftButtonOnClick: () => setInnerStep(INNER_STEP.upload),
        isLeftButtonActive: true,
        isRightButtonActive: true,
        rightButtonOnClick: () => {
          if (uploadedRecipients.length > 0 && hasErrorRecipients.length === 0) {
            setStep(REVIEW_IMPORT_STEP);
          }
        },
        rightButtonText: t('next'),
        rightButtonEndIcon: <Icon name="next" />,
        activeStepIndex: 1
      });
    }
  }, [uploadedRecipients, innerStep, isLoading]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  return innerStep === INNER_STEP.upload ? (
    <FileUploadArea
      handleFile={handleFile}
      status={uploadedRecipients?.length && 'success'}
      successButton={{
        onClick: () => {
          setUploadedRecipients([]);
        },
        text: t('scanningSuccessful')
      }}
      csvFileUrl={`${configs.publicUrl}/assets/docs/sgb-recipient-upload-template.csv`}
      xlsFileUrl={`${configs.publicUrl}/assets/docs/sgb-recipient-upload-template.csv`}
    />
  ) : (
    innerStep === INNER_STEP.table && (
      <>
        <S.TitleHeader>
          <Typography variant="body1Bold">
            {hasErrorRecipients.length ? (
              <S.IconWrapper>
                <Icon name="f-error" color="var(--color-error-default)" />
                <Trans
                  i18nKey="uploadSpreadSheetError"
                  values={{
                    errorContactCount: hasErrorRecipients.length,
                    type: t('recipient').toLocaleLowerCase()
                  }}
                  components={{ b: <S.ErrorText /> }}
                />
              </S.IconWrapper>
            ) : (
              <S.IconWrapper>
                <S.SuccessIconWrapper>
                  <Icon name="success" color="var(--color-bg-default)" size="lg" />
                </S.SuccessIconWrapper>
                <Typography variant="body1Bold">
                  {t('uploadSpreadSheetReadyToImport', {
                    totalContactCount: uploadedRecipients.length
                  })}
                </Typography>
              </S.IconWrapper>
            )}
          </Typography>
          {hasErrorRecipients.length > 0 && (
            <Typography variant="body2" color="fg" emphasis="weaker">
              {t('uploadSpreadSheetDescription', { type: t('recipients').toLocaleLowerCase() })}
            </Typography>
          )}
        </S.TitleHeader>

        <SpreadSheetRecipientTable
          recipients={uploadedRecipients}
          updateRecipients={setUploadedRecipients}
          onRemoveAll={() => setInnerStep(INNER_STEP.upload)}
          actionProps={{ isEditable: true }}
        />
        <S.InfoText>
          <Icon name="tooltip" size="sm" />
          {t('dragAndDropFileInfo')}
        </S.InfoText>
      </>
    )
  );
}

UploadSpreadSheet.propTypes = {
  isLoading: PropTypes.bool,
  setIsLoading: PropTypes.func,
  setUploadedRecipients: PropTypes.func,
  setActiveScene: PropTypes.func,
  uploadedRecipients: PropTypes.array,
  setFooterProps: PropTypes.func,
  setStep: PropTypes.func
};

export { UploadSpreadSheet };
